'use client'

import { cn } from '@/lib/utils/utils'
import { AppRouterInstance } from 'next/dist/shared/lib/app-router-context.shared-runtime'
import Link from 'next/link'
import { usePathname, useRouter } from 'next/navigation'
import { useEffect, useState } from 'react'
import { MdKeyboardArrowDown } from 'react-icons/md'
import { ThemeToggle } from '../theme/theme-toggle'
import { Flag } from '../ui/flag'
import { Flex, FlexCol } from '../ui/flex'
import { DEFAULT_LANG_CODE, getLocalizedUri, LangCode, LANGS } from './lang-context'

const changeLocale = (router: AppRouterInstance, pathname: string, newLangCode: LangCode) => {
  router.push(getNewLocalizedUri(pathname, newLangCode))
}
const getNewLocalizedUri = (pathname: string, newLangCode: LangCode) => {
  const locales = Object.keys(LANGS).join('|')
  const localeRegex = new RegExp(`^/(${locales})`)
  const pathUri = pathname.replace(localeRegex, '')
  return getLocalizedUri(pathUri, newLangCode)
}

export function LangToggle({ langCode } : {langCode: LangCode}) {

  const [currentLang, setCurrentLang] = useState(DEFAULT_LANG_CODE)
  const router = useRouter()
  const pathname = usePathname()

  useEffect(() => {
    setCurrentLang(langCode)
  }, [])


  const pickLang = (newLangCode: LangCode) => {
    setCurrentLang(newLangCode)
    changeLocale(router, pathname, newLangCode)
  }

  return (<div
    className='cursor-pointer text-sm opacity-40 transition-all hover:opacity-100'
    onClick={() => pickLang(currentLang === 'fr' ? 'en' : 'fr')}>
    {(currentLang === 'fr' ? 'en' : 'fr').toUpperCase()}
  </div>)
}

export function LangMenu({ langCode } : {langCode: LangCode}) {

  const [expanded, setExpanded] = useState(false)
  const [currentLang, setCurrentLang] = useState(DEFAULT_LANG_CODE)
  const router = useRouter()
  const pathname = usePathname()

  useEffect(() => {
    setCurrentLang(langCode)
  }, [])

  const pickLang = (newLangCode: LangCode) => {
    setCurrentLang(newLangCode)
    changeLocale(router, pathname, newLangCode)
  }

  return (
    <div className='relative select-none' onClick={() => setExpanded(exp => !exp)}>
      <Flex className='cursor-pointer items-center gap-0 text-sm opacity-40 transition-all hover:opacity-100'>
        <Flag countryOrLangCode={currentLang} width={20} height={20}/>
        <div id='ARROW' className='text-lg font-semibold opacity-70'>
          <MdKeyboardArrowDown size={16} className={cn('transition-all', expanded ? 'rotate-180' : '')} />
        </div>
      </Flex>
      {expanded && <div className='fixed left-0 top-0 z-10 h-[100vh] w-[100vw] bg-transparent'/>}
      {typeof window !== 'undefined' && <FlexCol className={cn('absolute left-[-25%] top-full z-20 mt-2 w-fit origin-top items-center gap-0 overflow-hidden rounded-md bg-white/90 p-1 text-sm shadow-sm transition-all dark:bg-gray-800/90 dark:shadow-header', expanded ? 'scale-y-100' : 'scale-y-0')}>
        <div className={cn('p-1 transition-all hover:opacity-100', currentLang === 'fr' ? 'font-medium' : 'cursor-pointer font-normal opacity-40')} onClick={() => pickLang('fr')}>
          <Flag countryOrLangCode='fr' width={20} height={20}/>
        </div>
        <div className={cn('p-1 transition-all hover:opacity-100', currentLang === 'en' ? 'font-medium' : 'cursor-pointer font-normal opacity-40')} onClick={() => pickLang('en')}>
          <Flag countryOrLangCode='en' width={20} height={20}/>
        </div>
      </FlexCol>}
    </div>
  )
}

export function LangMenuFlat({ className, themeToggle, langCode }: { className: string, themeToggle: boolean, langCode: LangCode }) {

  const [currentLang, setCurrentLang] = useState(DEFAULT_LANG_CODE)
  const router = useRouter()
  const pathname = usePathname()

  useEffect(() => {
    setCurrentLang(langCode)
  }, [])
  
  const pickLang = (newLangCode: LangCode) => {
    setCurrentLang(newLangCode)
    changeLocale(router, pathname, newLangCode)
  }

  return (
    <div className={cn('relative select-none', className)}>
      {<Flex className={cn('w-full items-center justify-center gap-8')}>
        <div className={cn('p-1 transition-all hover:opacity-100', currentLang === 'fr' ? 'font-medium opacity-75' : 'cursor-pointer font-normal opacity-40')} onClick={() => pickLang('fr')}>
          <Flag countryOrLangCode='fr' width={20} height={20}/>
        </div>
        <div className={cn('p-1 transition-all hover:opacity-100', currentLang === 'en' ? 'font-medium opacity-75' : 'cursor-pointer font-normal opacity-40')} onClick={() => pickLang('en')}>
          <Flag countryOrLangCode='en' width={20} height={20}/>
        </div>
        {themeToggle && <ThemeToggle />}
      </Flex>}
    </div>
  )
}

export function LangMenuFlatHTML({ className, langCode }: { className: string, langCode: LangCode }) {

  const pathname = usePathname()

  return (
    <div className={cn('relative select-none', className)}>
      {<Flex className={cn('w-full items-center justify-center gap-8')}>
        <Link className={cn('p-1 transition-all hover:opacity-100', langCode === 'fr' ? 'font-medium opacity-75' : 'cursor-pointer font-normal opacity-40')} href={getNewLocalizedUri(pathname, 'fr')}>FR</Link>
        <Link className={cn('p-1 transition-all hover:opacity-100', langCode === 'en' ? 'font-medium opacity-75' : 'cursor-pointer font-normal opacity-40')} href={getNewLocalizedUri(pathname, 'en')}>EN</Link>
      </Flex>}
    </div>
  )
}

export default LangMenu
